import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';

export default function AboutPage() {
  const commitments = [
    'Say what we\'ll do, do what we say',
    'Written timeline on every project',
    'Licensed and insured',
    'Regular progress updates via text/email',
  ];

  return (
    <main data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="bg-navy grain-overlay py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-copper text-xs font-inter uppercase tracking-widest">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white mt-4 leading-tight">
              30 Years of Expertise. Built to Do More.
            </h1>
            <p className="text-white/70 font-inter text-lg mt-6 leading-relaxed">
              C.A.B Enterprises of Texas, LLC was founded in January 2026 by a tradesperson with three decades of hands-on industry experience. Our mission: to deliver the kind of care and service the industry often fails to provide.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <span className="text-copper text-xs font-inter uppercase tracking-widest">
                  Our Story
                </span>
                <h2 className="text-4xl font-playfair font-bold text-navy mt-4">
                  From tradesperson to trusted contractor
                </h2>
              </div>
              
              <div className="space-y-6 text-navy/70 font-inter leading-relaxed">
                <p>
                  After 30 years working in the trades, our founder saw the same problems over and over: contractors who don't show up, jobs that drag on, and homeowners left in the dark. That's not how we work.
                </p>
                <p>
                  C.A.B Enterprises of Texas, LLC was built on a simple promise: every trade, one standard, no excuses. We handle everything from a single repair to complete home renovations—all under one roof, with the same commitment to showing up on time and doing the job right.
                </p>
                <p>
                  We're not just contractors—we're neighbors. Our team lives and works in the DFW metroplex. When we work on your home, every detail matters because we're part of the same community.
                </p>
                <p>
                  Whether you're a busy Millennial homeowner who expects text updates and transparent pricing, or a Gen X homeowner who's been burned by contractors before, we built this company for you.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1665069181618-5618c9b621ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjB0ZWFtJTIwYXJjaGl0ZWN0JTIwYmx1ZXByaW50cyUyMGhhcHB5JTIwaG9tZW93bmVyc3xlbnwwfHx8fDE3NzM0MzUyNTJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Construction team"
                className="w-full h-64 object-cover"
              />
              <img
                src="https://images.unsplash.com/photo-1581674662583-5e89b374fae6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB0ZWFtJTIwYXJjaGl0ZWN0JTIwYmx1ZXByaW50cyUyMGhhcHB5JTIwaG9tZW93bmVyc3xlbnwwfHx8fDE3NzM0MzUyNTJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Team at work"
                className="w-full h-64 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="py-24 bg-warm-accent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-copper text-xs font-inter uppercase tracking-widest">
              Our Commitments
            </span>
            <h2 className="text-4xl font-playfair font-bold text-navy mt-4">
              What you can expect from us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commitments.map((commitment, index) => (
              <div 
                key={index}
                className="bg-white p-6 border border-tan/20 flex items-start space-x-4"
                data-testid={`commitment-${index}`}
              >
                <CheckCircle className="w-6 h-6 text-copper flex-shrink-0 mt-0.5" />
                <span className="font-inter text-navy">{commitment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-copper text-xs font-inter uppercase tracking-widest">
              Our Team
            </span>
            <h2 className="text-4xl font-playfair font-bold text-navy mt-4">
              Texas-based. Texas-invested.
            </h2>
            <p className="text-navy/60 font-inter mt-4">
              Our team lives and works in the DFW Metroplex. When we work on your home, we know that every detail matters because we're neighbors, not just contractors.
            </p>
          </div>

          <div className="text-center">
            <Link to="/contact">
              <Button 
                data-testid="about-cta"
                className="bg-copper hover:bg-copper/90 text-white px-8 py-6 text-xs font-bold uppercase tracking-widest group"
              >
                Work With Us
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
